import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Button, IconButton,
  Typography, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem,
  Chip, Switch, FormControlLabel, Card,
  CardContent, Grid, CircularProgress,
  Avatar, FormControl, InputLabel, Select,
  InputAdornment, Tab, Tabs, Alert,
  List, ListItem, ListItemText, Divider,
  Tooltip, Collapse, Accordion, AccordionSummary,
  AccordionDetails, TableContainer
} from '@mui/material';
import {
  Edit, Delete, Add, LunchDining,
  Visibility, VisibilityOff, Search,
  LocalOffer, AccessTime, RestaurantMenu,
  Image as ImageIcon, Warning,
  ExpandMore, ExpandLess, AddCircle,
  RemoveCircle, EuroSymbol, Settings,
  AttachMoney, Fastfood, Save,
  AddPhotoAlternate, Cancel,
  KeyboardArrowDown, KeyboardArrowUp
} from '@mui/icons-material';
import { platAPI, categorieMenuAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const PlatsPage = () => {
  const [plats, setPlats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedPlats, setExpandedPlats] = useState({});
  const [expandedOptions, setExpandedOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    categorie: '',
    prix: '',
    est_disponible: true,
    image: null,
    imagePreview: null,
    options: []
  });

  // Fonction utilitaire pour extraire un tableau de la réponse API
  const extractArrayFromResponse = (data) => {
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === 'object') {
      const possibleArrayProps = ['results', 'data', 'items', 'plats', 'categories'];
      for (const prop of possibleArrayProps) {
        if (Array.isArray(data[prop])) {
          return data[prop];
        }
      }
    }
    return [];
  };

  const fetchPlats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platAPI.getAll();
      console.log('Réponse API plats:', response.data);
      
      const platsArray = extractArrayFromResponse(response.data);
      setPlats(platsArray);
      
      if (!Array.isArray(platsArray)) {
        console.warn('API plats ne retourne pas un tableau:', response.data);
        setError('Format de données inattendu pour les plats');
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Erreur lors du chargement des plats';
      setError(errorMessage);
      toast.error(errorMessage);
      setPlats([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categorieMenuAPI.getAll();
      const categoriesArray = extractArrayFromResponse(response.data);
      if (Array.isArray(categoriesArray)) {
        setCategories(categoriesArray.filter(c => c.est_active));
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors du chargement des catégories';
      toast.error(errorMessage);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchPlats();
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      categorie: '',
      prix: '',
      est_disponible: true,
      image: null,
      imagePreview: null,
      options: []
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (plat = null) => {
    if (plat) {
      console.log('Plat à éditer:', plat);
      setFormData({
        ...plat,
        categorie: plat.categorie,
        imagePreview: plat.image,
        options: plat.options || [],
        prix: plat.prix || ''
      });
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  // Fonctions pour gérer les options dans le formulaire
  const addNewOption = () => {
    const newOption = {
      id: Date.now(), // ID temporaire pour le frontend
      nom: '',
      prix_supplement: '',
      est_active: true
    };
    
    setFormData({
      ...formData,
      options: [...formData.options, newOption]
    });
  };

  const updateOption = (index, field, value) => {
    const updatedOptions = [...formData.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: field === 'prix_supplement' ? value : value
    };
    
    setFormData({
      ...formData,
      options: updatedOptions
    });
  };

  const removeOption = (index) => {
    const updatedOptions = formData.options.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      options: updatedOptions
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.nom.trim()) {
      toast.error('Le nom du plat est requis');
      return;
    }
    
    if (!formData.categorie) {
      toast.error('La catégorie est requise');
      return;
    }
    
    if (!formData.prix || parseFloat(formData.prix) <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    
    // Valider les options
    const invalidOptions = formData.options.filter(opt => 
      opt.nom.trim() === '' || 
      (opt.prix_supplement && parseFloat(opt.prix_supplement) < 0)
    );
    
    if (invalidOptions.length > 0) {
      toast.error('Veuillez corriger les options invalides');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Données de base
      formDataToSend.append('nom', formData.nom.trim());
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('prix', parseFloat(formData.prix).toFixed(2));
      formDataToSend.append('est_disponible', formData.est_disponible.toString());
      
      // Options - format propre pour le backend
      const optionsToSend = formData.options.map(opt => ({
        nom: opt.nom.trim(),
        prix_supplement: opt.prix_supplement ? parseFloat(opt.prix_supplement) : 0,
        est_active: opt.est_active
      }));
      
      if (optionsToSend.length > 0) {
        formDataToSend.append('options', JSON.stringify(optionsToSend));
        console.log('Options envoyées:', optionsToSend);
      }
      
      // Image
      if (formData.image && typeof formData.image !== 'string') {
        formDataToSend.append('image', formData.image);
      } else if (isEditing && formData.image && typeof formData.image === 'string') {
        // Garder l'image existante
        formDataToSend.append('image', formData.image);
      }
      
      console.log('Données envoyées:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }
      
      let response;
      if (isEditing) {
        response = await platAPI.update(formData.id, formDataToSend);
        toast.success('Plat modifié avec succès');
      } else {
        response = await platAPI.create(formDataToSend);
        toast.success('Plat créé avec succès');
      }
      
      console.log('Réponse serveur:', response.data);
      
      setOpenDialog(false);
      resetForm();
      fetchPlats();
      
    } catch (error) {
      console.error('Erreur complète:', error);
      console.error('Réponse d\'erreur:', error.response?.data);
      
      let errorMessage = 'Erreur lors de l\'enregistrement';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Extraire le message d'erreur
        if (typeof data === 'object') {
          // Essayer de trouver des erreurs spécifiques
          if (data.detail) {
            errorMessage = data.detail;
          } else if (data.error) {
            errorMessage = data.error;
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            // Lister toutes les erreurs de validation
            const errorList = [];
            for (const key in data) {
              if (Array.isArray(data[key])) {
                errorList.push(`${key}: ${data[key].join(', ')}`);
              } else if (typeof data[key] === 'string') {
                errorList.push(data[key]);
              }
            }
            
            if (errorList.length > 0) {
              errorMessage = errorList.join('; ');
            }
          }
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce plat ?')) {
      try {
        await platAPI.delete(id);
        toast.success('Plat supprimé avec succès');
        fetchPlats();
      } catch (error) {
        console.error('Erreur:', error);
        const errorMessage = error.response?.data?.detail || 
                            error.message ||
                            'Erreur lors de la suppression';
        toast.error(errorMessage);
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await platAPI.update(id, { est_disponible: !currentStatus });
      toast.success(`Plat ${!currentStatus ? 'activé' : 'désactivé'}`);
      fetchPlats();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de la modification du statut';
      toast.error(errorMessage);
    }
  };

  const togglePlatExpansion = (platId) => {
    setExpandedPlats(prev => ({
      ...prev,
      [platId]: !prev[platId]
    }));
  };

  // Fonctions utilitaires
  const getPlatsArray = () => {
    if (!Array.isArray(plats)) {
      console.warn('plats n\'est pas un tableau:', plats);
      return [];
    }
    return plats;
  };

  const getCategoriesArray = () => {
    if (!Array.isArray(categories)) {
      console.warn('categories n\'est pas un tableau:', categories);
      return [];
    }
    return categories;
  };

  const platsArray = getPlatsArray();
  const categoriesArray = getCategoriesArray();

  const filteredPlats = platsArray.filter(plat => {
    const matchesSearch = plat.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plat.code?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesCategory = selectedCategory === 'all' || plat.categorie === parseInt(selectedCategory);
    const matchesStatus = activeTab === 0 ? plat.est_disponible : !plat.est_disponible;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getCategoryName = (categorieId) => {
    const categorie = categoriesArray.find(c => c.id === categorieId);
    return categorie ? categorie.nom : 'Inconnue';
  };

  const totalRevenue = platsArray.reduce((sum, plat) => sum + (plat.prix * (plat.nombre_ventes || 0)), 0);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        p={3}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchPlats}
            >
              Réessayer
            </Button>
          }
        >
          <Typography variant="h6" gutterBottom>
            Erreur de chargement
          </Typography>
          <Typography>
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        p: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Header avec statistiques */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <LunchDining sx={{ mr: 2, verticalAlign: 'middle' }} />
              Plats
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez votre carte des plats
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher un plat..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { borderRadius: 1 }
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                height: 40,
                px: 3,
                borderRadius: 1
              }}
            >
              Nouveau Plat
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Plats
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {platsArray.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Disponibles
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {platsArray.filter(p => p.est_disponible).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Chiffre d'Affaires
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {totalRevenue.toLocaleString('fr-FR')} 
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Ventes Total
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {platsArray.reduce((sum, plat) => sum + (plat.nombre_ventes || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filtres et Tabs */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={selectedCategory}
              label="Catégorie"
              onChange={(e) => setSelectedCategory(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Toutes les catégories</MenuItem>
              {categoriesArray.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            '& .MuiTab-root': { minHeight: 48 }
          }}
        >
          <Tab 
            icon={<Visibility />} 
            label="Disponibles" 
            iconPosition="start"
          />
          <Tab 
            icon={<VisibilityOff />} 
            label="Indisponibles" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tableau des plats */}
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Catégorie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Prix</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Options</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Ventes</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlats.length > 0 ? (
                filteredPlats.map((plat) => (
                  <React.Fragment key={plat.id}>
                    <TableRow hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Avatar
                          src={plat.image}
                          variant="rounded"
                          sx={{ 
                            width: 60, 
                            height: 60,
                            borderRadius: 1,
                            backgroundColor: 'action.hover'
                          }}
                        >
                          {!plat.image && <RestaurantMenu />}
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          {plat.code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography fontWeight="medium">{plat.nom}</Typography>
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                          {plat.description || 'Aucune description'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={getCategoryName(plat.categorie)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center">
                          <LocalOffer fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography fontWeight="bold">
                            {parseFloat(plat.prix).toFixed(2)} 
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {plat.options && plat.options.length > 0 ? (
                            <Chip
                              size="small"
                              label={`${plat.options.length} option(s)`}
                              variant="outlined"
                              color="info"
                              onClick={() => togglePlatExpansion(plat.id)}
                              icon={expandedPlats[plat.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              sx={{ borderRadius: 1 }}
                            />
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              Aucune option
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography>
                          {plat.nombre_ventes || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={plat.est_disponible}
                              onChange={() => toggleStatus(plat.id, plat.est_disponible)}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              label={plat.est_disponible ? 'Disponible' : 'Indisponible'}
                              size="small"
                              color={plat.est_disponible ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }} align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenDialog(plat)}
                            size="small"
                            sx={{ 
                              '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(plat.id)}
                            size="small"
                            sx={{ 
                              '&:hover': { backgroundColor: 'error.light', color: 'white' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Ligne détaillée pour les options */}
                    {plat.options && plat.options.length > 0 && expandedPlats[plat.id] && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ backgroundColor: 'background.default', py: 2 }}>
                          <Box px={2}>
                            <Typography variant="subtitle2" gutterBottom color="textSecondary">
                              Options disponibles :
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {plat.options.map((option, index) => (
                                <Chip
                                  key={index}
                                  label={`${option.nom}${option.prix_supplement > 0 ? ` (+${option.prix_supplement})` : ''}`}
                                  size="small"
                                  color={option.est_active ? "success" : "default"}
                                  variant="outlined"
                                  icon={option.est_active ? <Fastfood /> : <RemoveCircle />}
                                  sx={{ borderRadius: 1 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <LunchDining sx={{ fontSize: 60, color: 'text.disabled' }} />
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm || selectedCategory !== 'all' 
                          ? 'Aucun plat ne correspond aux critères de recherche'
                          : 'Aucun plat trouvé'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre premier plat pour commencer
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 1 }}
                      >
                        Créer un plat
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog d'édition/création de plat avec options intégrées */}
      <Dialog 
        open={openDialog} 
        onClose={() => !submitting && setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <LunchDining />
              <Typography variant="h6">
                {isEditing ? 'Modifier le Plat' : 'Nouveau Plat'}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => !submitting && setOpenDialog(false)} 
              size="small"
              disabled={submitting}
            >
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" noValidate autoComplete="off">
            {/* Section principale du plat */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nom du plat"
                  fullWidth
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  error={!formData.nom.trim()}
                  helperText={!formData.nom.trim() ? 'Ce champ est requis' : ''}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.categorie}
                    label="Catégorie"
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                    disabled={submitting}
                    sx={{ borderRadius: 1 }}
                  >
                    {categoriesArray.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  multiline
                  rows={3}
                  fullWidth
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Décrivez votre plat..."
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Prix de base"
                  type="number"
                  fullWidth
                  value={formData.prix}
                  onChange={(e) => setFormData({...formData, prix: e.target.value})}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"></InputAdornment>,
                    inputProps: { 
                      step: "0.01",
                      min: "0",
                      placeholder: "0.00"
                    },
                    sx: { borderRadius: 1 }
                  }}
                  required
                  error={!formData.prix || parseFloat(formData.prix) <= 0}
                  helperText={!formData.prix || parseFloat(formData.prix) <= 0 ? 'Le prix doit être supérieur à 0' : ''}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Image du plat
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AddPhotoAlternate />}
                      disabled={submitting}
                      sx={{ borderRadius: 1 }}
                    >
                      Choisir une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={submitting}
                      />
                    </Button>
                    {formData.imagePreview && (
                      <Avatar
                        src={formData.imagePreview}
                        variant="rounded"
                        sx={{ 
                          width: 80, 
                          height: 80,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_disponible}
                      onChange={(e) => setFormData({...formData, est_disponible: e.target.checked})}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Plat disponible à la vente
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            {/* Section Options du plat */}
            <Accordion 
              expanded={expandedOptions} 
              onChange={() => setExpandedOptions(!expandedOptions)}
              elevation={2}
              disabled={submitting}
              sx={{ borderRadius: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Settings />
                  <Typography variant="h6">
                    Options du plat
                  </Typography>
                  <Chip 
                    label={`${formData.options.length} option(s)`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ ml: 2, borderRadius: 1 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Ajoutez des options ou suppléments pour ce plat (ex: extra fromage, sauce supplémentaire, etc.)
                </Typography>
                
                {formData.options.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
                    Aucune option définie. Cliquez sur "Ajouter une option" pour en créer une.
                  </Alert>
                ) : (
                  <List dense sx={{ mb: 2 }}>
                    {formData.options.map((option, index) => (
                      <ListItem 
                        key={option.id || index} 
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: 'background.paper'
                        }}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => removeOption(index)}
                            color="error"
                            size="small"
                            disabled={submitting}
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                          <Grid item xs={12} md={5}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Nom de l'option"
                              value={option.nom}
                              onChange={(e) => updateOption(index, 'nom', e.target.value)}
                              placeholder="Ex: Extra fromage"
                              disabled={submitting}
                              error={!option.nom.trim()}
                              helperText={!option.nom.trim() ? 'Requis' : ''}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Prix supplémentaire"
                              type="number"
                              value={option.prix_supplement}
                              onChange={(e) => updateOption(index, 'prix_supplement', e.target.value)}
                              InputProps={{
                                inputProps: { 
                                  step: "0.01", 
                                  min: "0",
                                  placeholder: "0.00"
                                }
                              }}
                              placeholder="0.00"
                              disabled={submitting}
                              error={option.prix_supplement && parseFloat(option.prix_supplement) < 0}
                              helperText={option.prix_supplement && parseFloat(option.prix_supplement) < 0 ? 'Doit être ≥ 0' : ''}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={option.est_active}
                                  onChange={(e) => updateOption(index, 'est_active', e.target.checked)}
                                  disabled={submitting}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {option.est_active ? "Active" : "Inactive"}
                                </Typography>
                              }
                            />
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                  </List>
                )}
                
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    startIcon={<AddCircle />}
                    onClick={addNewOption}
                    fullWidth
                    sx={{ 
                      maxWidth: 300,
                      borderRadius: 1
                    }}
                    disabled={submitting}
                  >
                    Ajouter une option
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Résumé des prix */}
            {formData.options.length > 0 && (
              <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom color="textSecondary">
                    Récapitulatif des prix
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Prix de base :</Typography>
                      <Typography variant="body2" fontWeight="bold">{parseFloat(formData.prix || 0).toFixed(2)}</Typography>
                    </Box>
                    {formData.options.filter(opt => opt.prix_supplement && parseFloat(opt.prix_supplement) > 0)
                      .map((option, index) => (
                      <Box key={index} display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">
                          {option.nom || `Option ${index + 1}`} :
                        </Typography>
                        <Typography variant="body2" color="primary">
                          +{parseFloat(option.prix_supplement || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subtitle1">Prix maximum possible :</Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                        {(
                          parseFloat(formData.prix || 0) + 
                          formData.options.reduce((sum, opt) => sum + parseFloat(opt.prix_supplement || 0), 0)
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            variant="outlined"
            disabled={submitting}
            sx={{ borderRadius: 1 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              submitting || 
              !formData.nom.trim() || 
              !formData.categorie || 
              !formData.prix || 
              parseFloat(formData.prix) <= 0 ||
              formData.options.some(opt => !opt.nom.trim() || (opt.prix_supplement && parseFloat(opt.prix_supplement) < 0))
            }
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
            sx={{ borderRadius: 1 }}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier le plat' : 'Créer le plat')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatsPage;